import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, X, Plus, ArrowRight, ArrowLeft, Film, CheckCircle2, ImageOff, Upload, Link2 } from "lucide-react";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";
import { trackMetaEvent, trackMetaCustomEvent } from "@/lib/meta";
import { LAST_HORIZON_CATEGORIES, LAST_HORIZON_STEPS, fmtINR } from "@/lib/lastHorizon";
import { useActiveMembership } from "@/hooks/useActiveMembership";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const labelCls = "text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55";
const emptyDriveLinks = () => Array.from({ length: 10 }, () => ({ url: "", caption: "" }));

function StepTabs({ step }) {
  return (
    <div className="flex flex-wrap gap-2 mb-8" data-testid="last-horizon-step-tabs">
      {LAST_HORIZON_STEPS.map((label, i) => (
        <span
          key={label}
          data-testid={`last-horizon-step-${i}`}
          className={`px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.12em] ${
            i === step ? "bg-ptf-primary text-ptf-bg" : i < step ? "bg-emerald-600/10 text-emerald-700" : "bg-ptf-primary/5 text-ptf-text/50"
          }`}
        >
          {i + 1}. {label}
        </span>
      ))}
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel = "Continue", saving }) {
  return (
    <div className="mt-8 flex items-center justify-between">
      {onBack ? (
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      ) : <span />}
      <button
        type={onNext ? "button" : "submit"}
        disabled={saving}
        data-testid="last-horizon-step-next"
        className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        onClick={onNext}
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} {nextLabel} {!saving && <ArrowRight className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function ProfileStep({ form, set, saving, onNext }) {
  const submit = (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Please choose a category."); return; }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="last-horizon-profile-step" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className={labelCls}>Full Name *</label><input required data-testid="lh-name" value={form.name} onChange={set("name")} className={inputCls} /></div>
        <div><label className={labelCls}>Phone *</label><input required data-testid="lh-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></div>
        <div><label className={labelCls}>City</label><input data-testid="lh-city" value={form.city} onChange={set("city")} className={inputCls} /></div>
        <div><label className={labelCls}>Country</label><input data-testid="lh-country" value={form.country} onChange={set("country")} className={inputCls} /></div>
        <div><label className={labelCls}>Age</label><input data-testid="lh-age" value={form.age} onChange={set("age")} className={inputCls} /></div>
      </div>
      <div>
        <label className={labelCls}>Category *</label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {LAST_HORIZON_CATEGORIES.map((c) => (
            <button
              key={c.value} type="button" data-testid={`lh-category-${c.value.toLowerCase()}`}
              onClick={() => set("category")({ target: { value: form.category === c.value ? "" : c.value } })}
              className={`border px-4 py-3 text-xs uppercase tracking-[0.1em] transition-colors ${form.category === c.value ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary/50"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div><label className={labelCls}>Camera / Device *</label><input required data-testid="lh-camera" value={form.camera_device} onChange={set("camera_device")} className={inputCls} placeholder="e.g. iPhone 15 Pro, Canon R6" /></div>
      <div><label className={labelCls}>Relevant Experience</label><textarea data-testid="lh-experience" value={form.experience} onChange={set("experience")} rows={3} className={inputCls} /></div>
      <StepNav saving={saving} />
    </form>
  );
}

function ProfilePhotoStep({ app, onUpdated, onBack, onNext }) {
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);
  const photo = app.profile_photo;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("The profile photo must be under 5MB."); return; }
    setUploading(true);
    setBroken(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/last-horizon/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the photo.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try { await api.delete("/member/last-horizon/profile-photo"); onUpdated(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="last-horizon-profile-photo-step">
      <p className="text-sm text-ptf-text/70 mb-5">
        Add a profile photo of yourself. If you consent to a public profile, this photo and your
        name may appear on the Foundation's public Last Horizon applicant gallery. Optional, but recommended.
      </p>
      {photo?.id ? (
        <div className="relative border border-ptf-primary/12 bg-white p-4 flex items-center gap-4 max-w-sm" data-testid="lh-profile-photo-uploaded">
          {broken ? (
            <div className="h-24 w-24 grid place-items-center bg-ptf-primary/5 text-ptf-text/30 shrink-0"><ImageOff className="h-6 w-6" /></div>
          ) : (
            <img src={resolveImg(photo.url)} alt="Profile" onError={() => setBroken(true)} className="h-24 w-24 object-cover shrink-0" />
          )}
          <button type="button" onClick={remove} data-testid="lh-profile-photo-delete" className="ml-auto bg-ptf-text/80 text-white p-1.5 hover:bg-red-600 transition-colors self-start"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid="lh-profile-photo-upload" className="border-2 border-dashed border-ptf-primary/25 h-32 w-32 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
          <span className="text-[0.6rem] uppercase tracking-[0.12em]">Add Photo</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

function ModeToggle({ mode, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="lh-submission-mode-toggle">
      <button
        type="button" onClick={() => onChange("upload")} data-testid="lh-mode-upload"
        className={`inline-flex items-center gap-2 border px-4 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${mode === "upload" ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary/50"}`}
      >
        <Upload className="h-3.5 w-3.5" /> Upload Files
      </button>
      <button
        type="button" onClick={() => onChange("drive_link")} data-testid="lh-mode-drive"
        className={`inline-flex items-center gap-2 border px-4 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${mode === "drive_link" ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary/50"}`}
      >
        <Link2 className="h-3.5 w-3.5" /> Paste Google Drive Link
      </button>
    </div>
  );
}

function PhotoTile({ photo, onCaption, onDelete }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="relative border border-ptf-primary/12 bg-white" data-testid={`lh-photo-${photo.id}`}>
      {broken ? (
        <div className="h-32 w-full grid place-items-center bg-ptf-primary/5 text-ptf-text/30"><ImageOff className="h-6 w-6" /></div>
      ) : (
        <img src={resolveImg(photo.url)} alt="Wildlife entry" onError={() => setBroken(true)} className="h-32 w-full object-cover" />
      )}
      <button type="button" onClick={() => onDelete(photo.id)} data-testid={`lh-photo-delete-${photo.id}`} className="absolute top-1.5 right-1.5 bg-ptf-text/80 text-white p-1 hover:bg-red-600 transition-colors">
        <X className="h-3.5 w-3.5" />
      </button>
      <input
        defaultValue={photo.caption}
        onBlur={(e) => onCaption(photo.id, e.target.value)}
        placeholder="Caption"
        data-testid={`lh-photo-caption-${photo.id}`}
        className="w-full text-xs px-2 py-1.5 border-t border-ptf-primary/10 outline-none focus:bg-ptf-primary/5"
      />
    </div>
  );
}

function UploadPhotos({ app, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const photos = app.photos || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Each photograph must be under 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/last-horizon/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the photograph.");
    } finally {
      setUploading(false);
    }
  };

  const onCaption = async (id, caption) => {
    try { await api.put(`/member/last-horizon/photos/${id}`, { caption }); } catch (err) { console.error("Failed to save photo caption:", err); }
  };

  const onDelete = async (id) => {
    try { await api.delete(`/member/last-horizon/photos/${id}`); onUpdated(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="last-horizon-photos-step">
      <p className="text-sm text-ptf-text/70 mb-5">Upload exactly ten wildlife photographs (JPEG, PNG or WebP, max 5MB each). {photos.length}/10 uploaded.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p) => <PhotoTile key={p.id} photo={p} onCaption={onCaption} onDelete={onDelete} />)}
        {photos.length < 10 && (
          <label data-testid="lh-photo-upload" className="border-2 border-dashed border-ptf-primary/25 h-32 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            <span className="text-[0.62rem] uppercase tracking-[0.12em]">Add Photo</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={uploading} />
          </label>
        )}
      </div>
    </div>
  );
}

function UploadVideo({ app, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const video = app.video;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("The video must be under 100MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/last-horizon/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the video.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try { await api.delete("/member/last-horizon/video"); onUpdated(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="last-horizon-video-step" className="mt-10">
      <p className="text-sm text-ptf-text/70 mb-5">Upload one 30-second wildlife video (MP4, MOV or WebM, max 100MB).</p>
      {video?.id ? (
        <div className="relative border border-ptf-primary/12 bg-white p-4 flex items-center gap-3" data-testid="lh-video-uploaded">
          <Film className="h-6 w-6 text-ptf-secondary" />
          <video src={resolveImg(video.url)} controls className="h-32 flex-1 bg-black" />
          <button type="button" onClick={remove} data-testid="lh-video-delete" className="bg-ptf-text/80 text-white p-1.5 hover:bg-red-600 transition-colors"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid="lh-video-upload" className="border-2 border-dashed border-ptf-primary/25 h-32 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
          <span className="text-[0.62rem] uppercase tracking-[0.12em]">Upload Video</span>
          <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function DriveLinks({ form, setDriveField }) {
  return (
    <div data-testid="last-horizon-drive-step">
      <p className="text-sm text-ptf-text/70 mb-5">Paste ten Google Drive photograph links and one Google Drive video link. Make sure sharing is set to "Anyone with the link can view".</p>
      <div className="space-y-3">
        {form.photo_links.map((link, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-2 items-end" data-testid={`lh-drive-photo-row-${i}`}>
            <div className="sm:col-span-2">
              <label className={labelCls}>Photo {i + 1} Drive Link *</label>
              <input
                data-testid={`lh-drive-photo-${i}`} value={link.url}
                onChange={(e) => setDriveField("photo_links", i, "url", e.target.value)}
                className={inputCls} placeholder="https://drive.google.com/..."
              />
            </div>
            <input
              data-testid={`lh-drive-photo-caption-${i}`} value={link.caption}
              onChange={(e) => setDriveField("photo_links", i, "caption", e.target.value)}
              className={inputCls} placeholder="Caption (optional)"
            />
          </div>
        ))}
      </div>
      <div className="mt-8 border-t border-ptf-primary/10 pt-6">
        <label className={labelCls}>Video Drive Link *</label>
        <input
          data-testid="lh-drive-video-link" value={form.video_link.url}
          onChange={(e) => setDriveField("video_link", null, "url", e.target.value)}
          className={inputCls} placeholder="https://drive.google.com/..."
        />
      </div>
    </div>
  );
}

function MediaStep({ app, form, setForm, saving, onUpdated, onBack, onNext, saveMode }) {
  const mode = form.submission_mode;

  const changeMode = async (next) => {
    if (next === mode) return;
    setForm((f) => ({ ...f, submission_mode: next }));
    await saveMode(next);
  };

  const setDriveField = (key, idx, field, value) => {
    setForm((f) => {
      if (key === "video_link") return { ...f, video_link: { ...f.video_link, [field]: value } };
      const links = [...f.photo_links];
      links[idx] = { ...links[idx], [field]: value };
      return { ...f, photo_links: links };
    });
  };

  const next = () => {
    if (mode === "upload") {
      if ((app.photos || []).length !== 10) { toast.error("Please upload exactly 10 photographs."); return; }
      if (!app.video?.id) { toast.error("Please upload your 30-second video."); return; }
      onNext();
      return;
    }
    const isDrive = (u) => typeof u === "string" && u.includes("drive.google.com");
    if (!form.photo_links.every((l) => isDrive(l.url))) { toast.error("Please paste all 10 Google Drive photo links."); return; }
    if (!isDrive(form.video_link.url)) { toast.error("Please paste a Google Drive video link."); return; }
    onNext();
  };

  return (
    <div data-testid="last-horizon-media-step">
      <ModeToggle mode={mode} onChange={changeMode} />
      {mode === "upload" ? (
        <>
          <UploadPhotos app={app} onUpdated={onUpdated} />
          <UploadVideo app={app} onUpdated={onUpdated} />
        </>
      ) : (
        <DriveLinks form={form} setDriveField={setDriveField} />
      )}
      <StepNav onBack={onBack} onNext={next} saving={saving} />
    </div>
  );
}

function DeclarationsStep({ form, set, saving, onBack, onNext }) {
  const checks = [
    ["declaration_original_work", "I declare that all submitted photographs and the video are my own original work."],
    ["declaration_rights", "I hold all necessary rights to the submitted content and grant Planet Trotter Foundation permission to use it for jury review."],
    ["declaration_rules", "I have read and agree to the Wildlife Filmmaking Challenge rules and eligibility requirements."],
  ];
  const submit = (e) => {
    e.preventDefault();
    if (!form.declaration_original_work || !form.declaration_rights || !form.declaration_rules || !form.consent) {
      toast.error("Please accept all required declarations and consent.");
      return;
    }
    if (!form.terms_accepted) {
      toast.error("Please read and accept the Terms & Conditions before proceeding to payment.");
      return;
    }
    onNext();
  };
  return (
    <form onSubmit={submit} data-testid="last-horizon-declarations-step" className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-5">
        <div><label className={labelCls}>Instagram</label><input data-testid="lh-instagram" value={form.instagram} onChange={set("instagram")} className={inputCls} placeholder="@handle" /></div>
        <div><label className={labelCls}>LinkedIn</label><input data-testid="lh-linkedin" value={form.linkedin} onChange={set("linkedin")} className={inputCls} placeholder="Profile URL" /></div>
        <div><label className={labelCls}>Facebook</label><input data-testid="lh-facebook" value={form.facebook} onChange={set("facebook")} className={inputCls} placeholder="Profile URL" /></div>
      </div>
      <div className="space-y-3 border-t border-ptf-primary/10 pt-5">
        {checks.map(([key, text]) => (
          <label key={key} className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer">
            <input type="checkbox" data-testid={`lh-${key}`} checked={form[key]} onChange={set(key)} className="mt-1 h-4 w-4 accent-ptf-primary" />
            {text}
          </label>
        ))}
        <label className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer">
          <input type="checkbox" data-testid="lh-consent" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-ptf-primary" />
          I consent to Planet Trotter Foundation processing my application for the Wildlife Filmmaking Challenge.
        </label>
        <label className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer border-t border-ptf-primary/10 pt-3">
          <input type="checkbox" data-testid="lh-public-profile-consent" checked={form.public_profile_consent} onChange={set("public_profile_consent")} className="mt-1 h-4 w-4 accent-ptf-primary" />
          I agree that my name, profile photograph and the social media profiles I provide may be displayed publicly on the Planet Trotter Foundation Last Horizon page.
        </label>
        <label className="flex items-start gap-3 text-sm text-ptf-text/75 cursor-pointer border-t border-ptf-primary/10 pt-3" data-testid="lh-terms-accepted-row">
          <input type="checkbox" data-testid="lh-terms_accepted" checked={form.terms_accepted} onChange={set("terms_accepted")} className="mt-1 h-4 w-4 accent-ptf-primary" />
          <span>
            I have read and accept the official{" "}
            <a href="/last-horizon/terms" target="_blank" rel="noopener noreferrer" data-testid="last-horizon-terms-link-wizard" className="underline font-semibold text-ptf-secondary hover:text-ptf-primary">
              Terms &amp; Conditions
            </a>{" "}
            for The Last Horizon. Confirmation is required before payment.
          </span>
        </label>
      </div>
      <StepNav onBack={onBack} saving={saving} />
    </form>
  );
}

function PaymentStep({ form, onBack }) {
  const [paying, setPaying] = useState(false);
  const { data: status } = useQuery({ queryKey: ["last-horizon-status"], queryFn: async () => (await api.get("/last-horizon/status")).data });
  const { data: creditInfo } = useQuery({ queryKey: ["last-horizon-credit"], queryFn: async () => (await api.get("/member/last-horizon/credit-eligibility")).data });
  const { hasActive, activeMembership, isLoading: membershipLoading } = useActiveMembership();
  const fee = status?.application_fee ?? 5000;
  const credit = Math.min(Number(creditInfo?.available_credit || 0), fee);
  const balance = Math.max(fee - credit, 0);

  const pay = async () => {
    setPaying(true);
    try {
      trackMetaCustomEvent("ApplicationSubmitted", { content_name: "The Last Horizon" });
      const { data } = await api.post("/member/last-horizon/checkout");
      if (data.payment === false) {
        toast.success("Your DAWN membership credit fully covers this application fee. No payment gateway needed.");
        window.location.reload();
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: "The Last Horizon: Wildlife Filmmaking Challenge",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        tracking: { content_name: "The Last Horizon", content_type: "competition", product_type: "competition_application" },
      });
      await api.post("/member/last-horizon/verify", resp);
      toast.success("Payment received. Now let's complete your media submission.");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response ? formatApiError(err.response.data?.detail) : err.message || "Could not complete the payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div data-testid="last-horizon-payment-step" className="space-y-5">
      <div className="bg-white border border-ptf-primary/12 p-6 space-y-2 text-sm">
        <p><strong>{form.name}</strong> · {form.email} · {form.phone}</p>
        <p className="text-ptf-text/60">{LAST_HORIZON_CATEGORIES.find((c) => c.value === form.category)?.label} · {form.camera_device}</p>
      </div>
      <div className="border-t border-ptf-primary/10 pt-5 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className={labelCls}>Application Fee</span>
          <span className="text-ptf-text/80 text-sm">{fmtINR(fee)}</span>
        </div>
        {credit > 0 && (
          <div className="flex items-center justify-between text-ptf-secondary text-xs" data-testid="lh-dawn-credit-line">
            <span>DAWN Membership Credit</span><span>&minus; {fmtINR(credit)}</span>
          </div>
        )}
        <div className="flex items-center justify-between pt-1.5 border-t border-ptf-primary/10">
          <span className={labelCls}>Balance Payable</span>
          <span className="font-display text-2xl text-ptf-primary" data-testid="lh-fee-amount">{fmtINR(balance)}</span>
        </div>
      </div>
      <p className="text-xs text-ptf-text/55">
        Payment does not guarantee selection. After paying, you'll complete your profile photo, ten photographs and a 30-second video.
      </p>
      {!membershipLoading && (
        <p data-testid="lh-membership-message" className="text-xs text-ptf-text/60 border-t border-ptf-primary/10 pt-4">
          {hasActive
            ? `Your existing Planet Trotter Foundation membership remains active according to its current validity (until ${activeMembership?.expiry_date}). This payment is only for your Last Horizon application and community access.`
            : "Successful payment will activate 1 year of Planet Trotter Foundation membership, including Learning & Training access and a member discount on eligible training programmes. This benefit is individual and non-transferable."}
        </p>
      )}
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button" onClick={pay} disabled={paying} data-testid="lh-pay-btn"
          className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        >
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {balance <= 0 ? "Confirm & Apply" : "Pay Application Fee"}
        </button>
      </div>
    </div>
  );
}

function ReviewSubmitStep({ app, form, onBack, onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const isDrive = form.submission_mode === "drive_link";

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/member/last-horizon/submit");
      toast.success("Application received for jury consideration.");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit your application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="last-horizon-review-step" className="space-y-5">
      <div className="bg-white border border-ptf-primary/12 p-6 space-y-2 text-sm">
        <p><strong>{form.name}</strong> · {form.email} · {form.phone}</p>
        <p className="text-ptf-text/60">{LAST_HORIZON_CATEGORIES.find((c) => c.value === form.category)?.label} · {form.camera_device}</p>
        <p className="text-ptf-text/60">
          {isDrive
            ? `${form.photo_links.filter((l) => l.url).length}/10 Google Drive photo links · ${form.video_link.url ? "1 video link" : "No video link"}`
            : `${(app.photos || []).length} photographs · ${app.video?.id ? "1 video uploaded" : "No video"}`}
        </p>
        <p className="text-ptf-text/60">Application fee: <span className="text-emerald-700 font-semibold">Paid</span></p>
      </div>
      <p className="text-xs text-ptf-text/55">Submitting sends your entry to the Foundation jury. It does not guarantee selection.</p>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-text/60 hover:text-ptf-primary transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button" onClick={submit} disabled={submitting} data-testid="lh-submit-btn"
          className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit Application
        </button>
      </div>
    </div>
  );
}

function isDriveUrl(u) {
  return typeof u === "string" && u.includes("drive.google.com");
}

function computeInitialStep(app) {
  const ageOk = Number(app.age) >= 18;
  const profileDone = app.name && app.phone && app.category && app.camera_device && ageOk;
  if (!profileDone) return 0;
  const declarationsDone = app.declaration_original_work && app.declaration_rights && app.declaration_rules && app.consent && app.terms_accepted;
  if (!declarationsDone) return 1;
  if (app.payment_status !== "paid") return 2;
  const mediaDone = app.submission_mode === "drive_link"
    ? (app.photo_links?.length === 10 && app.photo_links.every((l) => isDriveUrl(l.url)) && isDriveUrl(app.video_link?.url))
    : ((app.photos || []).length === 10 && !!app.video?.id);
  if (!mediaDone) return 3;
  return 5;
}

export default function LastHorizonWizard({ app, onUpdated }) {
  const [step, setStep] = useState(() => computeInitialStep(app));

  useEffect(() => {
    const flagKey = `lh_app_started_${app.id}`;
    if (app.status === "Draft" && !sessionStorage.getItem(flagKey)) {
      sessionStorage.setItem(flagKey, "1");
      trackMetaCustomEvent("ApplicationStarted", { content_name: "The Last Horizon" });
    }
  }, [app.id, app.status]);

  const [form, setForm] = useState({
    name: app.name || "", email: app.email || "", phone: app.phone || "", city: app.city || "",
    country: app.country || "India", age: app.age || "", category: app.category || "",
    camera_device: app.camera_device || "", experience: app.experience || "",
    instagram: app.instagram || "", linkedin: app.linkedin || "", facebook: app.facebook || "",
    declaration_original_work: app.declaration_original_work || false,
    declaration_rights: app.declaration_rights || false,
    declaration_rules: app.declaration_rules || false,
    consent: app.consent || false,
    public_profile_consent: app.public_profile_consent || false,
    terms_accepted: app.terms_accepted || false,
    submission_mode: app.submission_mode || "upload",
    photo_links: app.photo_links?.length === 10 ? app.photo_links : emptyDriveLinks(),
    video_link: app.video_link?.url ? app.video_link : { url: "", caption: "" },
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const persist = async (fields) => {
    await api.put("/member/last-horizon/application", { email: form.email, ...fields });
    onUpdated();
  };

  const saveAndGo = async (next, fields) => {
    setSaving(true);
    try {
      await persist(fields);
      setStep(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your details.");
    } finally {
      setSaving(false);
    }
  };

  const saveMode = async (mode) => {
    try {
      await persist({ submission_mode: mode });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update the submission mode.");
    }
  };

  const profileFields = () => ({
    name: form.name, phone: form.phone, city: form.city, country: form.country, age: form.age,
    category: form.category, camera_device: form.camera_device, experience: form.experience,
  });

  const declarationFields = () => ({
    instagram: form.instagram, linkedin: form.linkedin, facebook: form.facebook,
    declaration_original_work: form.declaration_original_work, declaration_rights: form.declaration_rights,
    declaration_rules: form.declaration_rules, consent: form.consent, public_profile_consent: form.public_profile_consent,
    terms_accepted: form.terms_accepted,
  });

  const mediaNext = () => {
    if (form.submission_mode === "drive_link") {
      return saveAndGo(5, { photo_links: form.photo_links, video_link: form.video_link });
    }
    setStep(5);
  };

  return (
    <div data-testid="last-horizon-wizard" className="bg-white border border-ptf-primary/12 p-6 md:p-10">
      <StepTabs step={step} />
      {step === 0 && <ProfileStep form={form} set={set} saving={saving} onNext={() => saveAndGo(1, profileFields())} />}
      {step === 1 && <DeclarationsStep form={form} set={set} saving={saving} onBack={() => setStep(0)} onNext={() => saveAndGo(2, declarationFields())} />}
      {step === 2 && <PaymentStep form={form} onBack={() => setStep(1)} />}
      {step === 3 && <ProfilePhotoStep app={app} onUpdated={onUpdated} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
      {step === 4 && (
        <MediaStep
          app={app} form={form} setForm={setForm} saving={saving} saveMode={saveMode} onUpdated={onUpdated}
          onBack={() => setStep(3)} onNext={mediaNext}
        />
      )}
      {step === 5 && <ReviewSubmitStep app={app} form={form} onBack={() => setStep(4)} onSubmitted={onUpdated} />}
    </div>
  );
}
